import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Mail, ArrowRight, Copy, Shield } from "lucide-react";
import { toast } from "sonner";
import { Footer } from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function FanPassPage() {
  const location = useLocation();
  const { displayName, club, sessionId } = location.state || {};
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Your Fan Pass is Ready - Afro Fanthem";
    window.scrollTo(0, 0);
  }, []);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/fan-pass/add-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim() })
      });
      if (res.ok) {
        setEmailSaved(true);
        toast.success("Recovery email saved!");
      } else {
        toast.error("Failed to save email. Try again.");
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success badge */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="fan-pass-headline">
              Your Fan Pass is <span className="text-[#FBBF24]">Ready</span>
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed max-w-lg mx-auto">
              You can now join the Afro Fanthem Fan Forum. To comment again in future, save your Fan Pass or add an email for a private sign-in link. No password or public profile needed.
            </p>
          </div>

          {/* Forum CTA */}
          <div className="bg-[#FBBF24]/10 border border-[#FBBF24]/30 p-6 sm:p-8 text-center mb-8">
            <h2 className="font-heading text-2xl text-white uppercase mb-3">Join the Debate</h2>
            <p className="text-gray-400 mb-6">Your forum access is active on this device. Jump in and share your voice.</p>
            <Link
              to="/forum"
              className="inline-flex items-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all"
              data-testid="fan-pass-forum-link"
            >
              Enter the Forum
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Optional email recovery */}
          {!emailSaved ? (
            <div className="bg-white/5 border border-white/10 p-6 sm:p-8 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-[#FBBF24]" />
                <h3 className="font-heading text-xl text-white uppercase">Add a Recovery Email</h3>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Adding an email lets you recover your Fan Pass on another device. We will not show it publicly.
              </p>
              <form onSubmit={handleAddEmail} className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-[#FBBF24]/50 focus:outline-none"
                  data-testid="recovery-email-input"
                />
                <button
                  type="submit"
                  disabled={saving || !email.trim()}
                  className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 text-sm hover:bg-[#F59E0B] transition-all disabled:opacity-50"
                  data-testid="save-email-btn"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </form>
              <p className="text-gray-600 text-xs mt-3">Optional. Skip this if you only use one device.</p>
            </div>
          ) : (
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-6 text-center mb-8">
              <Check className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
              <p className="text-gray-300">Recovery email saved. You can sign in from any device.</p>
            </div>
          )}

          {/* Privacy note */}
          <div className="bg-white/5 border border-white/10 p-5 mb-8">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Your Fan Pass is stored as a secure session on this device. No password or public profile is needed.
                  Forum posts use a display name you can set anytime.
                </p>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="text-center space-y-3">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors underline text-sm block">
              Return to Homepage
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
